import { DEFAULT_MODIFIERS, getRelicById } from './data/relics.js';
/**
 * Apply smooth diminishing returns using hyperbolic curve
 * Linear below soft cap, smooth hyperbolic curve above
 *
 * Formula above soft cap: effective = softCap + (cap - softCap) * ((x - softCap) / (x - softCap + k))
 * This ensures:
 * - Linear behavior below soft cap (no DR)
 * - Smooth transition at soft cap
 * - Approaches hard cap asymptotically
 */
function applySmoothDR(rawValue, softCap, hardCap, k) {
    if (rawValue <= 0)
        return 0;
    if (rawValue <= softCap) {
        // Linear below soft cap (no diminishing returns)
        return rawValue;
    }
    // Hyperbolic curve above soft cap
    // effective = softCap + (hardCap - softCap) * ((x - softCap) / (x - softCap + k))
    const excess = rawValue - softCap;
    const range = hardCap - softCap;
    const diminishedExcess = range * (excess / (excess + k));
    return Math.min(softCap + diminishedExcess, hardCap);
}
/**
 * Apply diminishing returns to crit chance
 * Linear below 50%, smooth hyperbolic curve above
 * Hard cap at 75%
 *
 * - Below 50%: linear (no DR)
 * - Above 50%: smooth hyperbolic curve
 * - Hard cap at 75%
 */
function applyCritDiminishingReturns(rawCritChance) {
    const SOFT_CAP = 0.5;
    const HARD_CAP = 0.75;
    // k controls the smoothness: smaller k = steeper curve
    // At raw=0.75 (50% excess), we want effective ≈ 0.625 (50% of range above soft cap)
    // 0.125 = 0.25 * (0.25 / (0.25 + k)) → k ≈ 0.25
    const K = 0.25;
    return applySmoothDR(rawCritChance, SOFT_CAP, HARD_CAP, K);
}
/**
 * Apply diminishing returns to damage bonus
 * Linear below 200%, smooth hyperbolic curve above
 * Hard cap at 500%
 *
 * - Below 200%: linear (no DR)
 * - Above 200%: smooth hyperbolic curve
 * - Hard cap at 500%
 */
function applyDamageDiminishingReturns(rawBonus) {
    const SOFT_CAP = 2.0; // 200% bonus
    const HARD_CAP = 5.0; // 500% max
    // k controls the smoothness: at raw=3.0 (1.0 excess), we want effective ≈ 2.5 (50% of range)
    // 0.5 = 3.0 * (1.0 / (1.0 + k)) → k ≈ 5.0, but let's use k=3.0 for smoother curve
    const K = 3.0;
    return applySmoothDR(rawBonus, SOFT_CAP, HARD_CAP, K);
}
/**
 * Apply diminishing returns to attack speed bonus
 * Linear below 100%, smooth hyperbolic curve above
 * Hard cap at 300%
 *
 * - Below 100%: linear (no DR)
 * - Above 100%: smooth hyperbolic curve
 * - Hard cap at 300%
 */
function applyAttackSpeedDiminishingReturns(rawBonus) {
    const SOFT_CAP = 1.0; // 100% bonus
    const HARD_CAP = 3.0; // 300% max
    // k controls the smoothness: at raw=2.0 (1.0 excess), we want effective ≈ 2.0 (50% of range)
    // 1.0 = 2.0 * (1.0 / (1.0 + k)) → k = 1.0
    const K = 1.0;
    return applySmoothDR(rawBonus, SOFT_CAP, HARD_CAP, K);
}
/**
 * Apply curse penalty to bonus accumulators
 */
function applyCurse(curse, bonuses, result) {
    switch (curse.stat) {
        case 'maxHpBonus':
        case 'maxHpMultiplier':
            // Convert multiplier to bonus: 0.8 becomes -0.2
            bonuses.maxHp += (curse.value - 1);
            break;
        case 'hpRegen':
            result.hpRegen = curse.value; // Set to 0 for vampiric touch
            break;
        case 'incomingDamage':
            // +25% incoming damage = -25% reduction
            bonuses.incomingDamageReduction -= (curse.value - 1);
            break;
        case 'damageBonus':
        case 'damageMultiplier':
            // Convert multiplier to bonus: 0.85 becomes -0.15
            bonuses.damage += (curse.value - 1);
            break;
        default:
            break;
    }
}
/**
 * Compute combined modifiers from active relics
 * Uses additive bonus system: base × (1 + sum of bonuses)
 * No more multiplicative power creep!
 */
export function computeModifiers(relics) {
    // Start with defaults
    const result = { ...DEFAULT_MODIFIERS };
    // Accumulators for additive bonuses
    const bonuses = {
        damage: 0,
        attackSpeed: 0,
        cooldown: 0,
        gold: 0,
        dust: 0,
        maxHp: 0,
        eliteDamage: 0,
        incomingDamageReduction: 0,
        splashRadius: 0,
        splashDamage: 0,
        chainDamage: 0,
        executeDamage: 0,
        critDamage: 0,
        mass: 0,
        knockbackResist: 0,
        ccResist: 0,
        dropRate: 0,
        relicQuality: 0,
        goldFind: 0,
        lowHpDamage: 0,
    };
    let rawCritChance = 0;
    for (const activeRelic of relics) {
        const def = getRelicById(activeRelic.id);
        if (!def)
            continue;
        if (def.category === 'synergy')
            continue;
        const mods = def.modifiers;
        // === ADDITIVE BONUSES ===
        if (mods.damageBonus !== undefined) {
            bonuses.damage += mods.damageBonus;
        }
        if (mods.attackSpeedBonus !== undefined) {
            bonuses.attackSpeed += mods.attackSpeedBonus;
        }
        if (mods.cooldownReduction !== undefined) {
            bonuses.cooldown += mods.cooldownReduction;
        }
        if (mods.goldBonus !== undefined) {
            bonuses.gold += mods.goldBonus;
        }
        if (mods.dustBonus !== undefined) {
            bonuses.dust += mods.dustBonus;
        }
        if (mods.maxHpBonus !== undefined) {
            bonuses.maxHp += mods.maxHpBonus;
        }
        if (mods.eliteDamageBonus !== undefined) {
            bonuses.eliteDamage += mods.eliteDamageBonus;
        }
        // === STACKABLE SECONDARY STATS ===
        // Splash - additive stacking (no more Math.max!)
        if (mods.splashRadiusBonus !== undefined) {
            bonuses.splashRadius += mods.splashRadiusBonus;
        }
        if (mods.splashDamagePercent !== undefined) {
            bonuses.splashDamage += mods.splashDamagePercent;
        }
        // Pierce - additive (unchanged)
        if (mods.pierceCount !== undefined) {
            result.pierceCount += mods.pierceCount;
        }
        // Chain - additive stacking
        if (mods.chainChance !== undefined) {
            result.chainChance += mods.chainChance;
        }
        if (mods.chainCount !== undefined) {
            result.chainCount += mods.chainCount;
        }
        if (mods.chainDamagePercent !== undefined) {
            bonuses.chainDamage += mods.chainDamagePercent;
        }
        // Execute - threshold uses max (logical), damage is additive
        if (mods.executeThreshold !== undefined) {
            result.executeThreshold = Math.max(result.executeThreshold, mods.executeThreshold);
        }
        if (mods.executeBonusDamage !== undefined) {
            bonuses.executeDamage += mods.executeBonusDamage;
        }
        // Crit - accumulate raw chance, apply DR later
        if (mods.critChance !== undefined) {
            rawCritChance += mods.critChance;
        }
        if (mods.critDamageBonus !== undefined) {
            bonuses.critDamage += mods.critDamageBonus;
        }
        // HP Regen - additive (unchanged)
        if (mods.hpRegen !== undefined) {
            result.hpRegen += mods.hpRegen;
        }
        // Incoming damage reduction
        if (mods.incomingDamageReduction !== undefined) {
            bonuses.incomingDamageReduction += mods.incomingDamageReduction;
        }
        // === PHYSICS-BASED DEFENSE ===
        if (mods.massBonus !== undefined) {
            bonuses.mass += mods.massBonus;
        }
        if (mods.knockbackResistance !== undefined) {
            bonuses.knockbackResist += mods.knockbackResistance;
        }
        if (mods.ccResistance !== undefined) {
            bonuses.ccResist += mods.ccResistance;
        }
        // === LUCK (META-REWARDS) ===
        if (mods.dropRateBonus !== undefined) {
            bonuses.dropRate += mods.dropRateBonus;
        }
        if (mods.relicQualityBonus !== undefined) {
            bonuses.relicQuality += mods.relicQualityBonus;
        }
        if (mods.goldFindBonus !== undefined) {
            bonuses.goldFind += mods.goldFindBonus;
        }
        // === CONDITIONAL ===
        if (mods.waveDamageBonus !== undefined) {
            result.waveDamageBonus += mods.waveDamageBonus;
        }
        if (mods.lowHpDamageBonus !== undefined) {
            bonuses.lowHpDamage += mods.lowHpDamageBonus;
        }
        if (mods.lowHpThreshold !== undefined) {
            result.lowHpThreshold = Math.max(result.lowHpThreshold, mods.lowHpThreshold);
        }
        // Apply curse penalties (unified handling)
        if (def.curse) {
            applyCurse(def.curse, bonuses, result);
        }
    }
    // === ASSIGN FINAL VALUES WITH CAPS ===
    // Additive bonuses with diminishing returns to prevent power creep
    result.damageBonus = applyDamageDiminishingReturns(bonuses.damage);
    result.attackSpeedBonus = applyAttackSpeedDiminishingReturns(bonuses.attackSpeed);
    result.cooldownReduction = Math.min(bonuses.cooldown, 0.75); // Cap at 75% CDR
    result.goldBonus = bonuses.gold;
    result.dustBonus = bonuses.dust;
    result.maxHpBonus = bonuses.maxHp;
    result.eliteDamageBonus = bonuses.eliteDamage;
    // Stackable stats with caps
    result.splashRadiusBonus = bonuses.splashRadius;
    result.splashDamagePercent = Math.min(bonuses.splashDamage, 1.0); // Cap at 100%
    result.chainChance = Math.min(result.chainChance, 1.0); // Cap at 100%
    result.chainDamagePercent = Math.min(bonuses.chainDamage, 1.0); // Cap at 100%
    result.executeBonusDamage = bonuses.executeDamage;
    // Crit with diminishing returns
    result.critChance = applyCritDiminishingReturns(rawCritChance);
    result.critDamageBonus = DEFAULT_MODIFIERS.critDamageBonus + bonuses.critDamage;
    // Defense
    result.incomingDamageReduction = bonuses.incomingDamageReduction;
    // Physics defense with caps
    result.massBonus = bonuses.mass;
    result.knockbackResistance = Math.min(bonuses.knockbackResist, 0.9); // Cap at 90%
    result.ccResistance = Math.min(bonuses.ccResist, 0.9); // Cap at 90%
    // Luck (meta-rewards)
    result.dropRateBonus = bonuses.dropRate;
    result.relicQualityBonus = bonuses.relicQuality;
    result.goldFindBonus = bonuses.goldFind;
    // Conditional
    result.lowHpDamageBonus = bonuses.lowHpDamage;
    return result;
}
/**
 * Calculate final damage against a target
 * Uses additive bonus system: base × (1 + sum of bonuses)
 */
export function calculateDamage(baseDamage, state, target, isCrit) {
    const mods = state.modifiers;
    let damage = baseDamage;
    // Base damage bonus (additive)
    damage *= (1 + mods.damageBonus);
    // Wave damage bonus (additive per wave)
    damage *= (1 + mods.waveDamageBonus * state.wavesCleared);
    // Elite damage bonus (additive)
    if (target.isElite) {
        damage *= (1 + mods.eliteDamageBonus);
    }
    // Execute bonus damage (additive)
    const hpPercent = target.hp / target.maxHp;
    if (mods.executeThreshold > 0 && hpPercent <= mods.executeThreshold) {
        damage *= (1 + mods.executeBonusDamage);
    }
    // Low HP fortress bonus (additive)
    const fortressHpPercent = state.fortressHp / state.fortressMaxHp;
    if (fortressHpPercent <= mods.lowHpThreshold) {
        damage *= (1 + mods.lowHpDamageBonus);
    }
    // Critical hit (uses critDamageBonus which includes base 0.5)
    if (isCrit) {
        damage *= (1 + mods.critDamageBonus);
    }
    return Math.floor(damage);
}
/**
 * Check if attack should crit (deterministic based on RNG)
 * Luck no longer affects combat - crit chance already has diminishing returns applied
 */
export function shouldCrit(critChance, rngValue) {
    return rngValue < critChance;
}
/**
 * Check if attack should chain (deterministic based on RNG)
 * Luck no longer affects combat - chain chance is capped at 100%
 */
export function shouldChain(chainChance, rngValue) {
    return rngValue < chainChance;
}
/**
 * Calculate drop chance with luck bonus
 * Used for materials, artifacts, relic quality
 */
export function calculateDropChance(baseChance, dropRateBonus) {
    return Math.min(baseChance * (1 + dropRateBonus), 1.0);
}
/**
 * Calculate gold earned with all bonuses
 */
export function calculateGoldEarned(baseGold, goldBonus, goldFindBonus) {
    return Math.floor(baseGold * (1 + goldBonus + goldFindBonus));
}
/**
 * Calculate dust earned with bonus
 */
export function calculateDustEarned(baseDust, dustBonus) {
    return Math.floor(baseDust * (1 + dustBonus));
}
/**
 * Calculate incoming damage after reduction
 * Negative reduction = more damage taken (from curses)
 */
export function calculateIncomingDamage(baseDamage, incomingDamageReduction) {
    return Math.floor(baseDamage * (1 - incomingDamageReduction));
}
//# sourceMappingURL=modifiers.js.map